/**
 * #855 — which incoming chat messages this viewer actually renders.
 *
 * Split out of `mount.ts` as a pure function because it carries the one
 * client-side invariant that keeps the SERVER's silent reject silent, and
 * `mount.ts` itself is un-unit-testable (it needs a real Pixi/WebGL context).
 * Everything here is local presentation: nothing reaches the server, and
 * nothing here can change what any other player sees.
 */

import type { WireSimState } from "@pit/protocol";

/**
 * Viewer-local chat display policy.
 *
 * `enabled: false` is the settings toggle (#855 item 2) — chat is off in
 * every direction, so nothing incoming is rendered at all. `mutedSeats` is the
 * per-seat, session-only mute list (#855 item 4): those seats are skipped
 * individually.
 */
export interface ChatDisplayPolicy {
  readonly enabled: boolean;
  readonly mutedSeats: readonly number[];
}

/** Chat on, nobody muted — the shipped default (#855 owner decision). */
export const DEFAULT_CHAT_DISPLAY_POLICY: ChatDisplayPolicy = {
  enabled: true,
  mutedSeats: [],
};

/**
 * The subset of a snapshot's `chat` array this viewer renders.
 *
 * Three silences, in order of bluntness:
 *   1. chat disabled → nothing;
 *   2. a muted seat → that seat's messages only;
 *   3. ⚠️ THE LOCAL SEAT, ALWAYS. The sender already saw their own line the
 *      instant they pressed Enter (`Renderer.echoLocalChat`), because the
 *      server may drop it silently (slur filter, rate limit) and the sender
 *      must not be able to tell. Rendering the server's echo as well would
 *      double every message you send; NOT echoing locally would turn a
 *      missing floater into an oracle for the filter. The two halves are one
 *      design and ship together.
 *
 * `ownSeat` is `null` before the seat is known (#731), which matches no
 * message — the same "a real seat is always a number" degradation the rest of
 * the renderer relies on.
 */
export function visibleChatForViewer(
  chat: WireSimState["chat"],
  policy: ChatDisplayPolicy,
  ownSeat: number | null,
): WireSimState["chat"] {
  if (!policy.enabled) return [];
  return chat.filter((m) => m.seat !== ownSeat && !policy.mutedSeats.includes(m.seat));
}
